import { useState, useEffect } from 'react';
import { X, Building2 } from 'lucide-react';
import { api, getErrorMessage } from '@/shared/services/api';
import { EmptyState } from '@/shared/components/EmptyState';
import { Spinner } from '@/shared/components/Spinner';
import toast from 'react-hot-toast';

interface Client {
  id: number;
  name: string;
  isActive: boolean;
}

interface AssignToClientModalProps {
  feedItemId: number;
  onClose: () => void;
}

const UPDATE_STATUSES = [
  'Pending',
  'Planned',
  'InProgress',
  'Completed',
  'NotApplicable',
  'OnHold',
];

const VULNERABILITY_LEVELS = ['None', 'Low', 'Medium', 'High', 'Critical'];

export function AssignToClientModal({ feedItemId, onClose }: AssignToClientModalProps) {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [status, setStatus] = useState('Pending');
  const [vulnerabilityLevel, setVulnerabilityLevel] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const loadClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data.filter((c: Client) => c.isActive));
    } catch (error) {
      toast.error('Failed to load clients');
      console.error('Error loading clients:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedClientId) {
      toast.error('Please select a client');
      return;
    }

    try {
      setLoading(true);
      await api.post('/clients/feed-item-status', {
        clientId: selectedClientId,
        feedItemId,
        status,
        vulnerabilityLevel: vulnerabilityLevel || null,
        plannedUpdateDate: plannedDate || null,
        completedUpdateDate: null,
        notes: notes || null,
      });

      toast.success('Feed item assigned to client successfully');
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign feed item'));
      console.error('Error assigning feed item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-lg animate-slide-up p-6 shadow-modal max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Building2 className="h-5 w-5 text-primary" />
            Assign to Client
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-icon btn-ghost btn-sm"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {clients.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No active clients available"
            description="Please create a client first in the Clients section"
          />
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="assign-client" className="label">
                Select Client *
              </label>
              <select
                id="assign-client"
                value={selectedClientId || ''}
                onChange={(e) => setSelectedClientId(Number(e.target.value))}
                className="input mt-1.5"
              >
                <option value="">-- Select a client --</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="assign-status" className="label">
                Status *
              </label>
              <select
                id="assign-status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input mt-1.5"
              >
                {UPDATE_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="assign-vulnerability" className="label">
                Vulnerability Level
              </label>
              <select
                id="assign-vulnerability"
                value={vulnerabilityLevel}
                onChange={(e) => setVulnerabilityLevel(e.target.value)}
                className="input mt-1.5"
              >
                <option value="">-- None --</option>
                {VULNERABILITY_LEVELS.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="assign-planned-date" className="label">
                Planned Update Date
              </label>
              <input
                id="assign-planned-date"
                type="date"
                value={plannedDate}
                onChange={(e) => setPlannedDate(e.target.value)}
                className="input mt-1.5"
              />
            </div>

            <div>
              <label htmlFor="assign-notes" className="label">
                Notes
              </label>
              <textarea
                id="assign-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="input mt-1.5 resize-none"
                placeholder="Add any notes about this update..."
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssign}
                disabled={loading || !selectedClientId}
                className="btn-primary flex-1"
              >
                {loading && <Spinner className="text-primary-foreground" />}
                {loading ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
